import { collection, limit, orderBy, where } from "firebase/firestore";
import moment from "moment";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { closeModal, controlToastState, openModal } from "risa-oasis-ui_v2";
import {
  batchUpdateDocuments,
  firestoreDB,
  FirestoreService,
} from "../../../api/firebase/firestoreService";
import { FirestoreCollectionReference } from "../../../api/firebase/references";
import AnalyticsDatePickerModal from "../../../components/modals/analyticsDatePicker/analyticsDatePickerModal";
import BulkReassignModal from "../../../components/modals/bulkReassignModal/bulkReassignModal";
import DocViewerWithSideNavigation from "../../../components/modals/docViewerWithSideNavigation/docViewerWithSideNavigation";
import ImageviewerModal from "../../../components/modals/imageViewerModal/imageviewerModal";
import ImageViewerWithBadgesModal from "../../../components/modals/imageViewerWithBadgesModal/imageViewerWithBadgesModal";
import OrderProcessingInfo from "../../../components/modals/OrderProcessingInfo/OrderProcessingInfo";
import { EXPORT_DATA_LIMIT } from "../../../constants/medicalPAbatch";
import { MedicalPaOrder } from "../../../data-model/medicalPaOrdersModel";
import { ScreenNamesForBigQuery } from "../../../enums/eventsData";
import { EventsName } from "../../../enums/eventsName";
import { LocalStorageKeys } from "../../../enums/localStorageKeys";
import { ModalId } from "../../../enums/modalId";
import { closeModal as closeReduxModal } from "../../../redux/slice/modalSlice";
import { AppDispatch, RootState } from "../../../redux/store/store";
import { logError } from "../../../utils/customLogger";
import {
  exportDataFromTableForMedicalPaOrders,
  exportDataToExcel,
} from "../../../utils/exportDataToExcel";
import { generateFullName } from "../../../utils/generateOrderDataForTable";
import { setItemInLocalStorage } from "../../../utils/localStorageHelper";
import { getOrgIdForFetchExternalWorklist } from "../../../utils/organizationHelper";
import OrderAssignmentInfo from "../medicalPaOrders/components/orderAssignmentInfo";
import MedicalPaOrdersTable from "./components/medicalPaOrdersTable";
import {
  fetchProcessedOrdersData,
  ProcessedOrdersData,
} from "./utils/fetchProcessedOrdersData";
import { handleOrdersProcessingInfoClick } from "./utils/handleOrdersProcessingInfoClick";

import { apiWorkerManager } from "../../../workerManagers/workerManager";
interface MedicalPaOrdersProps {
  onComplete?: boolean;
}

const MedicalPaOrders = (props: MedicalPaOrdersProps) => {
  const medicalPaOrdersTableRef = useRef<HTMLDivElement>(null);
  const [isBulkReassignModalOpen, setIsBulkReassignModalOpen] = useState(false);
  const [selectedOrderIds, setSelectedOrderIds] = useState<string[]>([]);
  const [showLoading, setShowLoading] = useState(false);
  const [, setProcessedOrdersToday] = useState<ProcessedOrdersData>({
    completion_percentage: 0,
    orders_processed: 0,
    orders_remaining: 0,
    time_taken: "",
    loading: false,
  });

  const [isSuccess, setIsSuccess] = useState(false);
  const { data: providersDetails } = useSelector(
    (state: RootState) => state.allProviders,
  );
  const { user } = useSelector(
    (state: RootState) => state.firebaseAuthentication,
  );
  const orgId = getOrgIdForFetchExternalWorklist();
  const { openedModalId, metaData } = useSelector(
    (state: RootState) => state.modal,
  );
  const dispatch = useDispatch<AppDispatch>();
  const [isUpdating, setIsUpdating] = useState(false);
  useEffect(() => {
    fetchProcessedOrdersToday();
    setItemInLocalStorage(
      LocalStorageKeys.PARENT_TABLE,
      window.location.pathname.split("/").slice(-1)[0],
    );
  }, []);

  useEffect(() => {
    if (isBulkReassignModalOpen) {
      openModal("bulk-reassign-modal");
    }
  }, [isBulkReassignModalOpen]);

  useEffect(() => {
    if (openedModalId === ModalId.IMAGE_VIEWER_MODAL) {
      openModal(ModalId.IMAGE_VIEWER_MODAL);
    }
    if (openedModalId === ModalId.DOC_VIEWER_WITH_SIDE_NAVIGATION) {
      openModal(ModalId.DOC_VIEWER_WITH_SIDE_NAVIGATION);
    }
    if (openedModalId === ModalId.ANALYTICS_DATE_PICKER_MODAL) {
      openModal(ModalId.ANALYTICS_DATE_PICKER_MODAL);
    }
    if (openedModalId === ModalId.IMAGE_VIEWER_WITH_BADGES_MODAL) {
      openModal(ModalId.IMAGE_VIEWER_WITH_BADGES_MODAL);
    }
    if (openedModalId === ModalId.ORDER_PROCESSING_INFO_MODAL) {
      openModal(ModalId.ORDER_PROCESSING_INFO_MODAL);
    }
  }, [openedModalId]);

  const handleDataFetchingForSelectedDateRange = async (
    startDate: Date,
    endDate: Date,
  ) => {
    try {
      setShowLoading(true);
      const startDateForExport = moment(startDate)
        .startOf("day")
        .format("YYYY-MM-DD HH:mm:ss");
      const endOfDayendDateForExport = moment(endDate)
        .endOf("day")
        .format("YYYY-MM-DD HH:mm:ss");
      const data = await FirestoreService.getDocumentsByQuery(
        FirestoreCollectionReference.medicalPaOrders(),
        [
          where("created_at", ">=", startDateForExport),
          where("created_at", "<=", endOfDayendDateForExport),
          where(
            "assigned_to.facility_id",
            "==",
            getOrgIdForFetchExternalWorklist(),
          ),
          orderBy("created_at", "desc"),
          limit(EXPORT_DATA_LIMIT),
        ],
      );
      const exportedData = exportDataFromTableForMedicalPaOrders(
        data as MedicalPaOrder[],
      );
      exportDataToExcel(
        exportedData,
        `medicalPaOrders-${moment().format("YYYY-MM-DD")}`,
      );
    } catch (error) {
    } finally {
      setShowLoading(false);
    }
  };

  const fetchProcessedOrdersToday = async () => {
    try {
      setProcessedOrdersToday((prev) => ({ ...prev, loading: true }));
      const data = await fetchProcessedOrdersData();
      setProcessedOrdersToday(data);
    } catch (error) {
      setProcessedOrdersToday({
        completion_percentage: 0,
        orders_processed: 0,
        orders_remaining: 0,
        time_taken: "",
        loading: false,
      });
    }
  };

  const handleReasignInBatchMode = async (selectedProvider: string) => {
    const selectedProviderDetails = providersDetails?.find(
      (provider) => provider.DocID === selectedProvider,
    );
    setIsUpdating(true);
    setIsSuccess(false);
    if (selectedProviderDetails) {
      const fullName = generateFullName(
        selectedProviderDetails.FirstName || "",
        "",
        selectedProviderDetails.LastName || "",
      );

      const assignedAt = moment().format("YYYY-MM-DDTHH:mm:ss.SSSSSS[+00:00]");

      const updates = selectedOrderIds.map((id) => ({
        collectionRef: collection(
          firestoreDB,
          FirestoreCollectionReference.medicalPaOrders(),
        ),
        docId: id,
        data: {
          "assigned_to.provider_name": fullName,
          "assigned_to.provider_id": selectedProviderDetails.DocID ?? "",
          "timestamps.assigned_at": assignedAt,
        },
      }));
      apiWorkerManager.enqueueAndProcess({
        event_name: EventsName.BULK_ASSIGN_BUTTON_SAVE_CLICKED,
        patient_id: "",
        order_id: "",
        user_id: user?.id ?? "",
        user_email: user?.email ?? "",
        org_id: orgId ?? "",
        additional_data: {
          screen_name: ScreenNamesForBigQuery.WORKLIST,
          user_id: user?.id ?? "",
        },
      });
      try {
        await batchUpdateDocuments(updates);
        setIsSuccess(true);
      } catch (error) {
        logError(error as Error, "Error in batch assignment");
        controlToastState("assign-order-failed");
      } finally {
        setIsUpdating(false);
        closeModal("bulk-reassign-modal");
        setIsBulkReassignModalOpen(false);
      }
    }
  };

  const handleDataChange = (data: any) => {
    setSelectedOrderIds(data);
  };

  return (
    <div className="medical-pa-orders--main-container flex h-full flex-col overflow-hidden pb-1">
      <div className="medical-pa-orders__header mb-4 flex items-center justify-between text-h11 font-bold text-primaryGray-1">
        All PA Orders
        <div className="flex cursor-pointer items-center gap-2 rounded-md bg-primaryGray-16 p-2 text-tiny font-normal text-primaryGray-1 transition-all duration-300 ease-in">
          <div className="orders-assignemnt-information border-r-2 border-primaryGray-7 hover:font-bold">
            <OrderAssignmentInfo />
          </div>
          <div
            className="view-orders-processing-information hover:font-bold"
            onClick={() => handleOrdersProcessingInfoClick(dispatch)}
          >
            View Orders Processing Infomation
          </div>
        </div>
      </div>

      <div className="medical-pa-orders__table-container flex-1 overflow-auto">
        <MedicalPaOrdersTable
          medicalPaOrdersTableRef={medicalPaOrdersTableRef}
          onDataChange={handleDataChange}
          onComplete={isSuccess}
          onBulkAssignButtonClick={() => {
            setIsBulkReassignModalOpen(true);
          }}
          exportingInProgress={showLoading}
        />
      </div>
      {isBulkReassignModalOpen && (
        <BulkReassignModal
          isLoading={isUpdating}
          onClose={() => setIsBulkReassignModalOpen(false)}
          onSave={(selectedProvider) =>
            handleReasignInBatchMode(selectedProvider)
          }
          shouldLogEvent={true}
        />
      )}

      <>
        <>
          {openedModalId === ModalId.IMAGE_VIEWER_MODAL && (
            <ImageviewerModal
              fileUrls={metaData?.fileUrls ?? []}
              filePaths={metaData?.filePaths ?? []}
              onClose={() => dispatch(closeReduxModal())}
              showLoader={metaData?.showLoader ?? false}
            />
          )}
          {openedModalId === ModalId.DOC_VIEWER_WITH_SIDE_NAVIGATION && (
            <DocViewerWithSideNavigation />
          )}
        </>
        {openedModalId === ModalId.ANALYTICS_DATE_PICKER_MODAL && (
          <AnalyticsDatePickerModal
            onClose={() => dispatch(closeReduxModal())}
            onDateRangeSelect={(startDate, endDate) => {
              handleDataFetchingForSelectedDateRange(startDate, endDate);
            }}
            maxRecords={EXPORT_DATA_LIMIT}
          />
        )}
        {openedModalId === ModalId.IMAGE_VIEWER_WITH_BADGES_MODAL && (
          <ImageViewerWithBadgesModal
            tabs={metaData?.tabs ?? []}
            showLoading={metaData?.showLoading ?? false}
            title="Write Back Trail"
          />
        )}
        {openedModalId === ModalId.ORDER_PROCESSING_INFO_MODAL && (
          <OrderProcessingInfo />
        )}
      </>
    </div>
  );
};

export default MedicalPaOrders;
